'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

interface ProactiveTimelineProps {
  className?: string;
  compact?: boolean;
}

/* ─────────────────────────────────────────────────────────
   Tile 1: Calendar-style reminder
   ───────────────────────────────────────────────────────── */
function ReminderTile({ visible, compact, t }: { visible: boolean; compact: boolean; t: ReturnType<typeof useTranslations> }) {
  return (
    <div
      className={`
        flex items-center gap-3 bg-white border border-teal-100 rounded-xl
        ${compact ? 'p-3' : 'p-3.5 sm:p-4'}
        transition-all duration-500 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      role="article"
    >
      {/* Calendar block */}
      <div className="flex-shrink-0 w-10 rounded-lg overflow-hidden border border-teal-200 text-center">
        <div className="bg-teal-700 text-white text-[8px] font-bold uppercase tracking-wider py-0.5">
          {t('reminder.month')}
        </div>
        <div className="bg-white text-teal-700 text-lg font-bold py-0.5">
          {t('reminder.day')}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-dark ${compact ? 'text-xs' : 'text-sm'}`}>
          {t('reminder.title')}
        </p>
        <p className={`text-text-muted ${compact ? 'text-[10px]' : 'text-[11px]'} mt-0.5`}>
          {t('reminder.source')}{' '}
          <span className="text-teal-500 font-medium">{t('reminder.chatDate')}</span>
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Tile 2: Profile-connected age milestone
   ───────────────────────────────────────────────────────── */
function MilestoneTile({ visible, compact, t }: { visible: boolean; compact: boolean; t: ReturnType<typeof useTranslations> }) {
  const tags = [t('milestone.tag1'), t('milestone.tag2'), t('milestone.tag3')];

  return (
    <div
      className={`
        bg-amber-light border border-amber/15 rounded-xl
        ${compact ? 'p-3' : 'p-3.5 sm:p-4'}
        transition-all duration-500 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      role="article"
    >
      {/* Profile tags row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber to-amber/80 flex items-center justify-center flex-shrink-0 text-white font-bold text-xs">
          {t('milestone.name')}
        </div>
        <div className="flex gap-1 flex-wrap">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] font-medium text-amber/80 bg-amber/12 px-1.5 py-0.5 rounded"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <p className={`text-dark leading-relaxed ${compact ? 'text-xs' : 'text-[13px]'}`}>
        {t('milestone.text')}{' '}
        <span className="text-[9px] font-semibold text-amber/70 bg-amber/15 px-1.5 py-0.5 rounded align-middle">
          {t('milestone.source')}
        </span>
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Tile 3: Pattern detection with data chips
   ───────────────────────────────────────────────────────── */
function PatternTile({ visible, compact, t }: { visible: boolean; compact: boolean; t: ReturnType<typeof useTranslations> }) {
  const chips = [
    { text: t('pattern.chip1'), matched: true },
    { text: t('pattern.chip1'), matched: true },
    { text: t('pattern.chip2'), matched: false },
    { text: t('pattern.chip1'), matched: true },
  ];

  return (
    <div
      className={`
        bg-coral-50 border border-coral-200/30 rounded-xl
        ${compact ? 'p-3' : 'p-3.5 sm:p-4'}
        transition-all duration-500 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      role="article"
    >
      {/* Data chips row */}
      <div className="flex gap-1 flex-wrap mb-2">
        {chips.map((chip, i) => (
          <span
            key={i}
            className={`
              inline-flex items-center gap-1 text-[10px] bg-white px-2 py-0.5 rounded-md
              ${chip.matched ? 'text-dark' : 'text-gray-400 opacity-40'}
            `}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                chip.matched ? 'bg-coral-400' : 'bg-gray-300'
              }`}
            />
            {chip.text}
          </span>
        ))}
      </div>

      {/* Connector arrow */}
      <div className="flex items-center gap-1.5 mb-2">
        <div className="flex-1 h-px bg-coral-300/25" />
        <span className="text-[8px] font-bold text-white bg-coral-400 px-1.5 py-0.5 rounded-md uppercase tracking-wide whitespace-nowrap">
          {t('pattern.matchCount')}
        </span>
        <div className="flex-1 h-px bg-coral-300/25" />
      </div>

      {/* Insight text */}
      <p className={`text-dark leading-relaxed ${compact ? 'text-xs' : 'text-[13px]'}`}>
        <strong className="text-coral-500">{t('pattern.insightBold')}</strong> {t('pattern.insight')}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */
export default function ProactiveTimeline({
  className = '',
  compact = false,
}: ProactiveTimelineProps) {
  const t = useTranslations('landing.proactiveTimeline');
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [visibleCards, setVisibleCards] = useState<boolean[]>(
    prefersReducedMotion ? [true, true, true] : [false, false, false],
  );

  useEffect(() => {
    if (prefersReducedMotion) return;

    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          [0, 1, 2].forEach((i) => {
            setTimeout(() => {
              setVisibleCards((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 300);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-2.5 ${className}`}
      aria-label={t('ariaLabel')}
    >
      <ReminderTile visible={visibleCards[0]} compact={compact} t={t} />
      <MilestoneTile visible={visibleCards[1]} compact={compact} t={t} />
      <PatternTile visible={visibleCards[2]} compact={compact} t={t} />
    </div>
  );
}
